<template>
  <div class="import-map-select-ui">
    <ImportMapSelectSkeleton v-if="isLoading" />
    <VFallbackText v-else-if="hasError">Something went wrong</VFallbackText>
    <VFallbackText v-else-if="!list.length">No saved mappings found</VFallbackText>
    <VDropdown
      v-else
      v-model="selectedId"
      label="Choose map config"
      arrow-icon-class-name="fr fr-chevron-down"
      :options="options"
      :disabled="isDisabled"
      @on-select="onSelect"
    />
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { TImportMap } from '~/shared/import/map/types';
  import type { IVDropdownOption } from '~/misc/v-dropdown/types';

  // Models
  import { ImportMapModel } from '~/modules/manager/models/import-map.model'; // TODO: replace with abstraction

  // Components
  import VDropdown from '~/misc/v-dropdown';
  import ImportMapSelectSkeleton from '~/shared/import/map/select/skeleton/ImportMapSelectSkeleton.vue';
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';

  const props = defineProps({
    companyId: {
      type: Number,
      required: true,
    },
    selectedMap: {
      type: Object as PropType<TImportMap | null>,
      default: null,
    },
    isDisabled: {
      type: Boolean,
      required: false,
    },
  });

  const emit = defineEmits<{
    (e: 'on-select', map: TImportMap | undefined): void;
  }>();

  const importMapModel = new ImportMapModel();

  const selectedId: Ref<number | null> = ref<number | null>(null);
  const list: Ref<TImportMap[]> = ref<TImportMap[]>([]);
  const isLoading: Ref<boolean> = ref<boolean>(false);
  const hasError: Ref<boolean> = ref<boolean>(false);

  const options: ComputedRef<IVDropdownOption<string, number>[]> = computed<
    IVDropdownOption<string, number>[]
  >(() => list.value.map((listItem: TImportMap) => ({ label: listItem.name, value: listItem.id })));

  const setSelectedId = (id: number | null) => {
    selectedId.value = id;
  };

  const setIsLoading = (state: boolean) => {
    isLoading.value = state;
  };

  const setHasError = (state: boolean) => {
    hasError.value = state;
  };

  const fetchMapList = async () => {
    try {
      setIsLoading(true);

      list.value = await importMapModel.index(props.companyId);
    } catch (error) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const onSelect = (option: IVDropdownOption<string, unknown>) => {
    const selectedMap: TImportMap | undefined = list.value.find(
      (item: TImportMap) => item.id === option.value
    );

    emit('on-select', selectedMap);
  };

  fetchMapList();

  watch(
    () => props.selectedMap?.id,
    (id?: number) => setSelectedId(id ?? null)
  );
</script>
